import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { PRODUCTS, startCheckout, checkoutError, getInventory } from "./products";
import { Reveal } from "./Reveal";

function ShopProduct({ p, index, stockInfo }) {
  const [loading, setLoading] = useState(false);
  const flip = index % 2 === 1;
  const slug = p.slug || p.name.toLowerCase().replace(/\s+/g, "-");
  const soldOut = stockInfo && stockInfo.stock <= 0;

  const buy = async () => {
    setLoading(true);
    try {
      await startCheckout(p.lookupKey);
    } catch (err) {
      checkoutError(err?.response?.data?.detail === "sold_out");
      setLoading(false);
    }
  };

  return (
    <article
      data-testid={`shop-product-${slug}`}
      className="grid items-center gap-10 md:grid-cols-2 md:gap-16"
    >
      <Reveal className={flip ? "md:order-2" : ""}>
        <div className="group relative overflow-hidden border border-white/10">
          <img
            src={p.image}
            alt={p.alt}
            className="aspect-[5/6] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
          />
          {p.preorder && (
            <span className="absolute left-4 top-4 border border-gold/40 bg-black/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-gold-light backdrop-blur-sm">
              Pre-order
            </span>
          )}
        </div>
      </Reveal>

      <Reveal delay={0.15} className={flip ? "md:order-1" : ""}>
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">
          Essence of Strength · N{index + 1}
        </p>
        <h2 className="mt-4 font-serif text-4xl font-light text-neutral-100 sm:text-5xl">{p.name}</h2>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500">
          {p.subtitle || "Eau de Parfum · 50ml"}
        </p>
        <div className="gold-rule mt-7 w-24 opacity-70" />
        <p className="mt-7 max-w-md text-base font-light leading-relaxed text-neutral-400">
          {p.description}
        </p>
        <div data-testid={`shop-notes-${slug}`} className="mt-7 flex flex-wrap gap-2">
          {p.notes.map((n) => (
            <span key={n} className="border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              {n}
            </span>
          ))}
        </div>
        <div className="mt-9 flex items-center gap-6">
          <span data-testid={`shop-price-${slug}`} className="font-mono text-2xl text-gold-light">{p.price}</span>
          {soldOut && (
            <span data-testid={`shop-stock-${slug}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/80">
              Sold out
            </span>
          )}
          <button
            onClick={buy}
            disabled={loading || soldOut}
            data-testid={`shop-preorder-${slug}`}
            className="group/btn flex items-center gap-2 border border-gold/40 px-8 py-4 text-[11px] uppercase tracking-[0.25em] text-gold-light transition-all duration-500 hover:border-gold hover:bg-gold/10 disabled:opacity-50"
          >
            {loading ? "Opening secure checkout" : soldOut ? "Sold out" : p.preorder ? "Pre-order now" : "Buy now"}
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-1" strokeWidth={1.5} />
            )}
          </button>
        </div>
        <p className="mt-5 text-xs font-light text-neutral-600">
          {p.preorder
            ? "Ships as soon as the first batch arrives. Secure checkout by Stripe."
            : "In stock and ready to send. Secure checkout by Stripe."}
        </p>
      </Reveal>
    </article>
  );
}

export default function ShopPage() {
  const [inventory, setInventory] = useState({});

  useEffect(() => {
    getInventory().then(setInventory).catch(() => {});
  }, []);

  return (
    <div data-testid="shop-page" className="min-h-screen bg-ink text-neutral-200">
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-10">
          <Link
            to="/"
            data-testid="shop-back-home"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-400 transition-colors duration-300 hover:text-gold-light"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Home
          </Link>
          <Link to="/" className="font-serif text-xs tracking-[0.22em] text-neutral-100 md:text-sm">
            TABI ESSENCE <span className="text-gold">OF STRENGTH</span>
          </Link>
          <span className="w-16" />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
        <Reveal className="mb-20 text-center md:mb-28">
          <p data-testid="shop-eyebrow" className="mb-6 font-mono text-[11px] uppercase tracking-[0.5em] text-gold">
            The First Collection
          </p>
          <h1 data-testid="shop-heading" className="font-serif text-4xl font-light text-neutral-100 sm:text-5xl lg:text-6xl">
            Essence of <em className="text-gold-light">Strength</em>
          </h1>
          <p className="mx-auto mt-6 max-w-lg text-base font-light leading-relaxed text-neutral-400">
            Two debut fragrances, composed in honour of a lasting legacy. In stock now — each
            bottle is wrapped with care and dispatched to you.
          </p>
        </Reveal>

        <div className="space-y-24 md:space-y-36">
          {PRODUCTS.map((p, i) => (
            <ShopProduct key={p.lookupKey} p={p} index={i} stockInfo={inventory[p.lookupKey]} />
          ))}
        </div>

        <Reveal className="mt-24 text-center md:mt-36">
          <p className="font-serif text-lg italic text-neutral-500">
            Honouring the past, inspiring the future.
          </p>
        </Reveal>
      </main>
    </div>
  );
}
